<template>
  <b-navbar toggleable type="light" variant="light" class="p-3">
    <div class="container">
      <b-navbar-brand class="title">
        {{ t("title") }}
      </b-navbar-brand>
    </div>
  </b-navbar>
  <div v-if="loader" class="container-fluid message-container-align">
    <b-spinner
      style="width: 6rem; height: 6rem; color: #003778"
      label="Large Spinner"
    ></b-spinner>
  </div>
  <div
    v-if="invalidIDFlag || maxPasswordExceeded || errorFlag || unAuthorizedFlag"
    class="container-fluid message-container-align"
  >
    <h2 v-if="unAuthorizedFlag">{{ t("form.unauthorized") }}</h2>
    <h2 v-if="maxPasswordExceeded">
      {{ t("form.yourlinkhasexpired") }}
    </h2>
    <h2 v-if="invalidIDFlag">{{ t("form.invalidrequest") }}</h2>
    <h2 v-if="errorFlag && !unAuthorizedFlag">
      {{ t("form.unabletoprocesstherequest") }}
    </h2>
  </div>
  <div
    v-if="
      !loader &&
      !invalidIDFlag &&
      !maxPasswordExceeded &&
      !errorFlag &&
      !unAuthorizedFlag
    "
    class="container-fluid"
  >
    <p v-if="store.state.isSubscriber" class="container">
      {{ t("form.expirydaysmessage") }}
      <a href="">{{ expiryDays }} {{ t("form.days") }}</a
      >.
    </p>
    <p v-if="!store.state.isSubscriber" class="container">
      {{ t("form.expirydaysmessageInvoice") }}
      <a href="">{{ expiryDays }} {{ t("form.days") }}</a
      >.
    </p>
    <div class="container p-3" style="min-height: 363px">
      <b-row class="my-1">
        <b-form-group
          :label="formLabel"
          label-for="password-input"
          class="custom-label"
        >
          <b-col sm="4" class="password-input-container">
            <b-form-input
              id="password-input"
              :placeholder="formPlaceholder"
              v-model="Password"
              :type="showPassword ? 'password' : 'text'"
              :state="PasswordState"
              class="custom-placeholder"
              required
            ></b-form-input>
            <a
              @click="togglePasswordVisibility"
              class="password-toggle-icon"
              :class="[
                {
                  'error-icon':
                    PasswordState === false ||
                    PasswordError === 'mismatch' ||
                    PasswordError === 'empty',
                },
                { 'eye-icon-align': PasswordState === true },
              ]"
            >
              <i class="fas fa-eye"></i>
            </a>

            <b-form-invalid-feedback :state="PasswordState">
              {{ formError }}
            </b-form-invalid-feedback>
          </b-col>
        </b-form-group>
      </b-row>

      <b-row class="my-1">
        <b-form-group
          :label="t('form.captcha')"
          label-for="captcha-input"
          class="custom-label"
        >
          <b-col sm="4">
            <b-form-input
              id="captcha-input"
              type="text"
              :placeholder="t('form.entercaptcha')"
              v-model="captchaInput"
              :state="captchaState"
              class="custom-placeholder"
              required
            ></b-form-input>
            <b-form-invalid-feedback v-if="captchaError" :state="captchaState">
              {{ captchaErrorMessage }}
            </b-form-invalid-feedback>
          </b-col>
        </b-form-group>
      </b-row>

      <Recaptcha @captcha-updated="handleCaptchaUpdate" />
      <div class="spacer20"></div>
      <b-button pill class="custom-button" @click="validateForm">{{
        t("form.submit")
      }}</b-button>
      <div class="spacer20"></div>
      <span id="previousDownloads" class="txtSize15">
        {{ t("download.previousdownloads") }}: {{ downloadCount }}
      </span>
      <div class="spacer5"></div>
      <span id="remainingDownloads" class="txtSize15">
        {{ t("download.remainingdownloads") }}: {{ remainingDownloads }}
      </span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted, computed } from "vue";
import CryptoJS from "crypto-js";
import Recaptcha from "./Recaptcha.vue";
import router from "@/router";
import { useI18n } from "vue-i18n";
import axios from "axios";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { generateChecksum } from "../services/generateChecksum";
import { decryptData } from "../services/decryptData";
const otpRequired = ref(false);
const expiryDays = computed(() => userDocument?.value?.ExpiryDays);
const secretKey = process.env.VUE_APP_ENCRYPT_DECRYPT_SECRET_KEY;
const JwtSecretKey = process.env.VUE_APP_JWT_KEY;
const errorFlag = ref(false);
const unAuthorizedFlag = ref(false);
const store = useStore();
const loader = ref(false);
const { t, locale } = useI18n();
const Password = ref("");
const captchaInput = ref("");
const PasswordState = ref<boolean | null>(null);
const captchaState = ref<boolean | null>(null);
const captchaText = ref("");
const captchaError = ref<"empty" | "mismatch" | null>(null);
const PasswordError = ref<"empty" | "mismatch" | null>(null);
const userDocument = ref<any | null>(null);
const invoiceDetail = ref<any | null>(null);
const downloadCount = computed(() => invoiceDetail?.value?.DownloadCount);
const remainingDownloads = computed(
  () => userDocument?.value?.MaxDownloads - invoiceDetail?.value?.DownloadCount
);
//const linkExpiredFlag = ref(false);
const invalidIDFlag = ref(false);
const baseUrl = process.env.VUE_APP_BASE_API_URL;
const handleCaptchaUpdate = (text: string) => {
  captchaText.value = text;
};
const key = CryptoJS.enc.Utf8.parse(process.env.VUE_APP_AES_KEY);
const hashPhoneNumber = (password: string) => {
  const encrypted = CryptoJS.AES.encrypt(password, key, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7,
  });
  return encrypted.toString();
};
const showPassword = ref(true);
const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value;
};
const route = useRoute();
const userId = ref(route.query.p as string);
const maxPasswordExceeded = ref(false);

const getInvoiceCount = async (token: any) => {
  const checksumString = `${userId.value}`;
  const checksum = generateChecksum(checksumString);
  try {
    const invoiceCountDetail = await axios.get(
      `${baseUrl}/invoice/count/${userId.value}`,
      {
        headers: { Authorization: `Bearer ${token}`, "X-Checksum": checksum },
      }
    );
    const encryptedData = invoiceCountDetail.data;
    const decryptedText = decryptData(encryptedData);
    const receivedChecksum = invoiceCountDetail.headers["x-file-checksum"];
    const calculatedChecksum = generateChecksum(invoiceCountDetail.data);
    const invoiceData = JSON.parse(decryptedText);
    if (
      receivedChecksum === calculatedChecksum &&
      invoiceData.UserId === userId.value &&
      invoiceData.IsLocked == false
    ) {
      invoiceDetail.value = invoiceData;
      loader.value = false;
    } else {
      loader.value = false;
      errorFlag.value = true;
    }
  } catch (error: any) {
    loader.value = false;
    errorFlag.value = true;
    console.error("Failed to fetch invoice details:", error);
  }
};
const getToken = async (token: any) => {
  token = "";
  try {
    const encryptedDataValue = {
      UserId: userId.value,
      SecretKey: JwtSecretKey,
      timestamp: new Date().toISOString(),
    };
    const checksumString = Object.values(encryptedDataValue).join("-");
    const checksum = generateChecksum(checksumString);
    const dataString = JSON.stringify(encryptedDataValue);
    const encryptedDataString = CryptoJS.AES.encrypt(
      dataString,
      secretKey
    ).toString();
    const tokenResponse = await axios.post(
      `${baseUrl}/auth/generate-token`,
      { encryptedDataString },
      {
        headers: {
          "Content-Type": "application/json",
          "X-Checksum": checksum,
        },
      }
    );
    token = tokenResponse.data.token;
    setTokenInSessionStorage(token, 23);
    return token;
  } catch (error: any) {
    console.error("Failed to generate token:", error);
    if (error.response) {
      if (error.response.status === 404 || error.response.status === 401) {
        unAuthorizedFlag.value = true;
      } else {
        errorFlag.value = true;
        throw new Error("Failed to generate token. Please try again.");
      }
    }
  }
};
const invalidPasswordAttemptCount = async () => {
  let token = getTokenFromSessionStorage();

  if (!token) {
    token = await getToken(token);
  }
  if (
    invoiceDetail?.value?.InvalidPasswordAttempts + 1 >
    process.env.VUE_APP_MAX_PASSWORD_ATTEMPT_ALLOWED
  ) {
    maxPasswordExceeded.value = true;
  }
  await getInvoiceCount(token);
};

const validateForm = async () => {
  let isValid = true;
  PasswordState.value = null;
  if (!Password.value) {
    PasswordError.value = "empty";
    PasswordState.value = false;
    isValid = false;
  } else {
    PasswordError.value = "";
  }
  captchaState.value = captchaInput.value === captchaText.value;
  if (!captchaInput.value) {
    captchaError.value = "empty";
    captchaState.value = false;
    isValid = false;
  } else if (captchaInput.value !== captchaText.value) {
    captchaError.value = "mismatch";
    captchaState.value = false;
    isValid = false;
  }
  if (captchaError.value === "empty" || captchaError.value === "mismatch") {
    if (!Password.value) {
      PasswordState.value = false;
    } else {
      PasswordState.value = null;
    }
  }
  if (captchaState.value && PasswordState.value !== false) {
    const hashedPhoneNumber = hashPhoneNumber(Password.value);
    let token = getTokenFromSessionStorage();
    if (!token) {
      token = await getToken(token);
    }
    const encryptedDataValue = {
      UserId: userId.value,
      password: hashedPhoneNumber,
      timestamp: new Date().toISOString(),
    };
    const checksumString = Object.values(encryptedDataValue).join("-");
    const checksum = generateChecksum(checksumString);
    const dataString = JSON.stringify(encryptedDataValue);
    const encryptedDataString = CryptoJS.AES.encrypt(
      dataString,
      secretKey
    ).toString();
    const response = await axios.post(
      `${baseUrl}/check-password`,
      { encryptedDataString },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Checksum": checksum,
        },
      }
    );
    const passwordMatchFlag = ref("");
    const encryptedData = response.data;
    const decryptedText = decryptData(encryptedData);
    const receivedChecksum = response.headers["x-file-checksum"];
    const calculatedChecksum = generateChecksum(response.data);
    const invoiceData = JSON.parse(decryptedText);
    otpRequired.value = invoiceData.OtpRequired;
    if (
      receivedChecksum === calculatedChecksum &&
      invoiceData.UserId === userId.value
    ) {
      passwordMatchFlag.value = invoiceData.Password;
      if (passwordMatchFlag.value) {
        PasswordState.value = true;
        PasswordError.value = "";
      }
      loader.value = false;
    } else {
      loader.value = false;
      errorFlag.value = true;
    }
    if (!passwordMatchFlag.value) {
      PasswordError.value = "mismatch";
      PasswordState.value = false;
      invalidPasswordAttemptCount();
      isValid = false;
    } else {
      PasswordState.value = true;
    }
  }
  if (isValid && captchaState.value && PasswordState.value === true) {
    invalidPasswordAttemptCount();
    store.commit("setUserID", userDocument.value.Id);
    store.commit("setAuth", true);
    setTimeout(() => {
      router.push({ name: "PDFDownloadPage", query: { p: userId.value } });
    }, 300);
  }
  return isValid;
};

watch(Password, () => (PasswordState.value = null));
watch(captchaInput, () => {
  captchaError.value = null;
  captchaState.value = null;
});

const setTokenInSessionStorage = (token: any, expiresIn: number) => {
  const expirationTime = Date.now() + expiresIn * 3600 * 1000;
  sessionStorage.setItem(`token_${userId.value}`, token);
  sessionStorage.setItem(
    `tokenExpiration_${userId.value}`,
    expirationTime.toString()
  );
};

const getTokenFromSessionStorage = () => {
  const token = sessionStorage.getItem(`token_${userId.value}`);
  const expirationTime = parseInt(
    sessionStorage.getItem(`tokenExpiration_${userId.value}`) || "0"
  );

  if (Date.now() > expirationTime) {
    sessionStorage.removeItem(`token_${userId.value}`);
    sessionStorage.removeItem(`tokenExpiration_${userId.value}`);
    return null;
  }

  return token;
};

const fetchUserData = async () => {
  store.commit("setAuth", false);
  try {
    loader.value = true;
    let token = getTokenFromSessionStorage();

    if (!token) {
      token = await getToken(token);
    }
    const checksumString = `${userId.value}`;
    const checksum = generateChecksum(checksumString);
    try {
      const invoiceResponse = await axios.get(
        `${baseUrl}/invoice/${userId.value}`,
        {
          headers: { Authorization: `Bearer ${token}`, "X-Checksum": checksum },
        }
      );
      const encryptedData = invoiceResponse.data;
      const decryptedText = decryptData(encryptedData);
      const receivedChecksum = invoiceResponse.headers["x-file-checksum"];
      const calculatedChecksum = generateChecksum(invoiceResponse.data);
      const invoiceData = JSON.parse(decryptedText);
      if (
        receivedChecksum === calculatedChecksum &&
        invoiceData.UserId === userId.value
      ) {
        userDocument.value = invoiceData;
        store.commit("setSubscriber", !invoiceData.OtpRequired);
        store.commit("setDisplayTitle", true);
      } else {
        loader.value = false;
        errorFlag.value = true;
      }
    } catch (error: any) {
      console.error("Failed to fetch user data", error);
      if (error.response && error.response.status === 404) {
        invalidIDFlag.value = true;
        loader.value = false;
        throw new Error(
          "User ID not found. Please check the ID and try again."
        );
      } else {
        errorFlag.value = true;
        loader.value = false;
        throw new Error("Failed to fetch user data. Please try again later.");
      }
    }
    await getInvoiceCount(token);
    if (
      invoiceDetail?.value?.InvalidPasswordAttempts + 1 >
      process.env.VUE_APP_MAX_PASSWORD_ATTEMPT_ALLOWED
    ) {
      maxPasswordExceeded.value = true;
    }
  } catch (error: any) {
    error.value =
      error.message || "An unexpected error occurred. Please try again later.";
    loader.value = false;
  } finally {
    loader.value = false;
  }
};

onMounted(fetchUserData);

const formLabel = computed(() => {
  return locale.value === "en"
    ? userDocument.value?.PasswordPromptEnglish
    : userDocument.value?.PasswordPromptFrench || "";
});

const formPlaceholder = computed(() => {
  let label = formLabel.value;
  if (label) {
    label = label.replace(/your /i, "").replace(/votre /i, "le ");
  }
  return label || "";
});

const formError = computed(() => {
  if (PasswordError.value === "empty") {
    return t("form.mandatoryError");
  } else if (PasswordError.value === "mismatch") {
    return t("form.incorrectPassword");
  } else {
    return "";
  }
});

const captchaErrorMessage = computed(() => {
  if (captchaError.value === "empty") {
    return t("form.pleaseentercaptcha");
  } else {
    return t("form.captchadoesnotmatch");
  }
});
</script>

<style scoped>
@media screen and (max-width: 767px) {
  .title {
    font-size: 20px !important;
    margin-left: -15px;
  }
}
.title {
  font-family: "bellslim_mediumregular", Helvetica, Arial, sans-serif;
  font-size: 25px;
  font-weight: bold;
}
.spacer20 {
  padding-top: 20px;
}

.custom-label {
  color: #555;
  font-weight: bold;
  font-size: 16px;
  font-family: "Helvetica", Arial, sans-serif;
}

.custom-button {
  color: #fff;
  background-color: #003778;
  border: 2px solid #003778;
  font-size: 15px;
  padding: 10px 32px;
  text-align: center;
  cursor: pointer;
  border-radius: 24px;
  line-height: 1;
  --bs-btn-hover-bg: #003778;
}

.message-container-align {
  min-height: 363px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.password-input-container {
  position: relative;
  /* Enables positioning of child elements */
}

.password-toggle-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #6c757d;
}

.password-toggle-icon.error-icon {
  right: 31px;
  top: 31%;
}

.password-toggle-icon.eye-icon-align {
  right: 31px;
  top: 52%;
}

.custom-placeholder::placeholder {
  font-size: 12.5px;
}

input[type="password"]::-ms-reveal,
input[type="password"]::-ms-clear,
input[type="password"]::-webkit-clear-button,
input[type="password"]::-webkit-inner-spin-button {
  display: none;
}
</style>
